import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { HelmetProvider } from 'react-helmet-async';
import 'react-toastify/dist/ReactToastify.css';
import { setUser, logout } from '../store/slices/authSlice';
import api from '../api';

// Components
import Navbar from './Navbar';
import Footer from './Footer';

// Pages (lazy loaded)
const Home        = lazy(() => import('../pages/Home'));
const Products    = lazy(() => import('../pages/Products'));
const ProductDetail = lazy(() => import('../pages/ProductDetail'));
const Cart        = lazy(() => import('../pages/Cart'));
const Checkout    = lazy(() => import('../pages/Checkout'));
const Login       = lazy(() => import('../pages/Login'));
const Register    = lazy(() => import('../pages/Register'));
const Profile     = lazy(() => import('../pages/Profile'));
const Orders      = lazy(() => import('../pages/Orders'));
const About       = lazy(() => import('../pages/About'));
const Contact     = lazy(() => import('../pages/Contact'));
const FAQ         = lazy(() => import('../pages/FAQ'));
const Blog        = lazy(() => import('../pages/Blog'));
const BlogPost    = lazy(() => import('../pages/BlogPost'));
const NotFound    = lazy(() => import('../pages/NotFound'));
const ForgotPassword = lazy(() => import('../pages/ForgotPassword'));
const ResetPassword  = lazy(() => import('../pages/ResetPassword'));

// Admin pages
const AdminLayout       = lazy(() => import('../pages/admin/AdminLayout'));
const Dashboard         = lazy(() => import('../pages/admin/Dashboard'));
const ManageProducts    = lazy(() => import('../pages/admin/ManageProducts'));
const ManageOrders      = lazy(() => import('../pages/admin/ManageOrders'));
const ManageUsers       = lazy(() => import('../pages/admin/ManageUsers'));
const AdminSettings     = lazy(() => import('../pages/admin/Settings'));
const AdminShipping     = lazy(() => import('../pages/admin/Shipping'));
const AdminBlog         = lazy(() => import('../pages/admin/ManageBlog'));
const AdminFAQ          = lazy(() => import('../pages/admin/ManageFAQ'));
const AdminCategories   = lazy(() => import('../pages/admin/ManageCategories'));
const AdminContact      = lazy(() => import('../pages/admin/ManageContact'));
const ManageComments    = lazy(() => import('../pages/admin/ManageComments'));

// Admin hub pages (tabbed)
const AdminProducts     = lazy(() => import('../pages/admin/AdminProducts'));
const AdminManagement   = lazy(() => import('../pages/admin/AdminManagement'));
const AdminBlogHub      = lazy(() => import('../pages/admin/AdminBlogHub'));

// Cooperatore layout
const CooperatoreLayout = lazy(() => import('../pages/admin/CooperatoreLayout'));

const Loader = () => (
  <div className="flex items-center justify-center min-h-64">
    <div className="spinner w-10 h-10 border-4"></div>
  </div>
);

function ProtectedRoute({ children, adminOnly = false, shopOnly = false }) {
  const { token, role } = useSelector(s => s.auth);
  if (!token) return <Navigate to="/login" replace />;
  if (adminOnly && role !== 'admin') return <Navigate to="/" replace />;
  if (shopOnly && role !== 'admin' && role !== 'cooperatore') return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  const dispatch = useDispatch();
  const { token } = useSelector(s => s.auth);

  // Validate token on app mount — log out if token is invalid/expired
  useEffect(() => {
    if (token) {
      api.get('/auth/me')
        .then(r => dispatch(setUser(r.data)))
        .catch(() => {
          // Token is invalid or expired — clear it so the user sees login
          dispatch(logout());
        });
    }
  }, [token, dispatch]);

  return (
    <HelmetProvider>
    <BrowserRouter>
      <div className="page-wrapper">
        <Navbar />
        <main className="page-content">
          <Suspense fallback={<Loader />}>
            <Routes>
              {/* Public */}
              <Route path="/"            element={<Home />} />
              <Route path="/products"    element={<Products />} />
              <Route path="/products/:id" element={<ProductDetail />} />
              <Route path="/about"       element={<About />} />
              <Route path="/contact"     element={<Contact />} />
              <Route path="/faq"         element={<FAQ />} />
              <Route path="/blog"        element={<Blog />} />
              <Route path="/blog/:slug"  element={<BlogPost />} />
              <Route path="/cart"        element={<Cart />} />
              <Route path="/login"       element={<Login />} />
              <Route path="/register"    element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password"  element={<ResetPassword />} />

              {/* Protected — logged in */}
              <Route path="/checkout"    element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
              <Route path="/profile"     element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/orders"      element={<ProtectedRoute><Orders /></ProtectedRoute>} />

              {/* Admin */}
              <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
                <Route index          element={<Dashboard />} />
                {/* Hub pages */}
                <Route path="shop"       element={<AdminProducts />} />
                <Route path="management" element={<AdminManagement />} />
                <Route path="content"    element={<AdminBlogHub />} />
                {/* Legacy individual routes (kept for direct links) */}
                <Route path="products"   element={<ManageProducts />} />
                <Route path="orders"     element={<ManageOrders />} />
                <Route path="users"      element={<ManageUsers />} />
                <Route path="settings"   element={<AdminSettings />} />
                <Route path="shipping"   element={<AdminShipping />} />
                <Route path="blog"       element={<AdminBlog />} />
                <Route path="faq"        element={<AdminFAQ />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="contact"    element={<AdminContact />} />
                <Route path="comments"   element={<ManageComments />} />
              </Route>

              {/* Cooperatore — shop staff: products + orders only */}
              <Route path="/cooperatore" element={<ProtectedRoute shopOnly><CooperatoreLayout /></ProtectedRoute>}>
                <Route index             element={<Navigate to="/cooperatore/products" replace />} />
                <Route path="products"   element={<ManageProducts />} />
                <Route path="orders"     element={<ManageOrders />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>

      <ToastContainer
        position="bottom-right"
        autoClose={3500}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        theme="light"
        toastClassName="rounded-xl shadow-lg"
      />
    </BrowserRouter>
    </HelmetProvider>
  );
}
